import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveTable as MinimalistResponsiveTable } from './UI/Table';
import ActionButton from './UI/ActionButton';

/**
 * ResponsiveTable - A responsive table component with minimalist styling
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of objects representing the table data
 * @param {Array} props.columns - Array of column configuration objects
 * @param {string} props.keyField - The field in data objects to use as unique key
 * @param {Function} props.onRowClick - Optional callback when a row is clicked
 * @param {string} props.emptyMessage - Message to display when data array is empty
 * @param {boolean} props.isLoading - Whether the data is currently loading
 * @param {Array} props.actions - Array of action button configurations
 * @param {string} props.className - Additional CSS class for the table container
 */
const ResponsiveTable = ({
  data = [],
  columns = [],
  keyField = 'id',
  onRowClick,
  emptyMessage = 'No data available',
  isLoading = false,
  actions = [],
  className = '',
  headerClassName = '',
  rowClassName = null,
  sort,
  onSortChange,
  // Less padding, for a narrow panel.
  compact = false,
}) => {
  const [activeRow, setActiveRow] = useState(null);

  // Convert existing columns to minimalist table format
  const minimalistColumns = columns.map(col => ({
    header: col.header,
    accessor: col.field,
    cell: col.formatter ? row => col.formatter(row[col.field], row) : undefined,
    width: col.width,
    // Clicking the header sorts by the field's value, or by sortValue(row)
    // where the field is not what the column shows; sortable: false opts out.
    sortable: col.sortable,
    sortValue: col.sortValue,
  }));

  // Distinct colors cycled across action buttons that don't bring their own styling
  const actionColorCycle = [
    'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300',
    'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300',
    'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300',
    'bg-purple-50 text-purple-600 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-300',
    'bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300',
  ];

  // Add actions column if needed
  if (actions && actions.length > 0) {
    minimalistColumns.push({
      header: 'Actions',
      accessor: '_actions',
      cell: row => (
        <div className="flex flex-wrap items-center gap-2">
          {actions
            .filter(action => !action.condition || action.condition(row))
            .map((action, idx) => (
              <ActionButton
                key={idx}
                onClick={e => {
                  e.stopPropagation();
                  return action.onClick(row);
                }}
                className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md ${
                  action.className || actionColorCycle[idx % actionColorCycle.length]
                }`}
                disabled={action.disabled ? action.disabled(row) : false}
                title={action.tooltip || action.label}
                busyLabel={action.busyLabel}
              >
                {action.icon && <span className="mr-1">{action.icon}</span>}
                {action.label}
              </ActionButton>
            ))}
        </div>
      )
    });
  }

  // Handle row click events
  const handleRowClick = row => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  return (
    <div className="min-w-full">
      <MinimalistResponsiveTable
        columns={minimalistColumns}
        data={data}
        isLoading={isLoading}
        emptyMessage={emptyMessage}
        className={className}
        onRowClick={handleRowClick ? row => handleRowClick(row) : undefined}
        headerClassName={headerClassName}
        rowClassName={rowClassName}
        sort={sort}
        onSortChange={onSortChange}
        compact={compact}
      />
    </div>
  );
};

ResponsiveTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      formatter: PropTypes.func,
      headerRenderer: PropTypes.func,
      className: PropTypes.string,
      headerClassName: PropTypes.string,
      showInMobile: PropTypes.bool,
      width: PropTypes.string,
      sortable: PropTypes.bool,
      sortValue: PropTypes.func,
    })
  ).isRequired,
  keyField: PropTypes.string,
  onRowClick: PropTypes.func,
  emptyMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      condition: PropTypes.func,
      disabled: PropTypes.func,
      className: PropTypes.string,
      icon: PropTypes.node,
      tooltip: PropTypes.string
    })
  ),
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  // A list split into pages sorts the whole list itself (useTableSort) and
  // passes its sort here; otherwise the table sorts the rows it is given.
  sort: PropTypes.object,
  onSortChange: PropTypes.func,
};

export default ResponsiveTable;
